import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuthStore } from '@/store/auth-store';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { CheckCircle2, Database, ShieldCheck, Sparkles } from 'lucide-react';

export default function LoginPage() {
  const navigate = useNavigate();
  const login = useAuthStore((state) => state.login);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await login(email, password);
      toast.success('Login successful');
      navigate('/dashboard');
    } catch (error: any) {
      console.error('Login error:', error);
      const errorMessage = error?.response?.data?.error?.message
        || error?.response?.data?.message
        || error?.message
        || 'Login failed';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      data-testid="login-split-layout"
      className="min-h-screen bg-surface-base px-4 py-6 md:px-6"
    >
      <div className="mx-auto grid min-h-[calc(100vh-3rem)] w-full max-w-6xl overflow-hidden rounded-2xl border bg-surface-elevated shadow-panel lg:grid-cols-[1.1fr_1fr]">
        <section className="relative hidden flex-col justify-between border-r bg-gradient-to-br from-surface-focus via-surface-base to-surface-base p-10 lg:flex">
          <div className="space-y-5">
            <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-primary text-primary-foreground">
              <Database className="h-5 w-5" />
            </div>
            <div className="space-y-3">
              <h1 className="font-display text-4xl font-semibold tracking-tight text-foreground">
                DW Command Center
              </h1>
              <p className="max-w-md text-sm leading-6 text-muted-foreground">
                Unified control room for ingestion, pipeline orchestration, and enterprise analytics
                across North Kalimantan data domains.
              </p>
            </div>
          </div>

          <div className="space-y-3">
            {[
              { icon: ShieldCheck, text: 'Enterprise-grade access control and secure session handling' },
              { icon: CheckCircle2, text: 'Operational visibility for pipelines and data quality checks' },
              { icon: Sparkles, text: 'AI-assisted pipeline planning with governed data contexts' },
            ].map((item) => (
              <div key={item.text} className="flex items-start gap-3 rounded-xl border bg-surface-elevated/80 px-4 py-3">
                <item.icon className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <p className="text-sm text-muted-foreground">{item.text}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="flex items-center justify-center p-5 md:p-8">
          <Card className="w-full max-w-md border-border/70 bg-card/95 shadow-none">
            <CardHeader className="space-y-2">
              <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-primary/15 text-primary lg:hidden">
                <Database className="h-5 w-5" />
              </div>
              <CardTitle className="font-display text-3xl tracking-tight">Sign in</CardTitle>
              <CardDescription>
                Authenticate to access the operational workspace.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="admin@datawarehouse.local"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={isLoading}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={isLoading}
                  />
                </div>
                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading ? 'Signing in...' : 'Sign in'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </section>
      </div>
    </div>
  );
}
